import dynamic from 'next/dynamic';

import React, { useState, useEffect } from 'react';
const Button = dynamic(() => import('@/components/ui/Button'));
import Modal from "@/components/ui/Modal";
import Pagination from "@/components/ui/Pagination";
import { query, collection, where, getDocs } from 'firebase/firestore';
import { db } from "@/firebase";
import { Timestamp } from 'firebase/firestore';

const SearchNodePurchasedModal = ({ isOpen, onClose }) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchOption, setSearchOption] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [searchWarning, setSearchWarning] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items to display per page
  const [emptySearchWarning, setEmptySearchWarning] = useState(false);
  const [invalidInputWarning, setInvalidInputWarning] = useState('');

  const walletRegex = /^0x[a-fA-F0-9]{40}$/;
  const uidRegex = /^[a-zA-Z0-9]{28}$/;

  useEffect(() => {
    setCurrentPage(1); // Reset current page when search changes
  }, [searchResult]); // Reset current page when search results change

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

   // Pagination calculation to get items for the current page
   const indexOfLastItem = currentPage * itemsPerPage;
   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
   const currentItems = searchResult.slice(indexOfFirstItem, indexOfLastItem);

  const formatDateTime = (date) => {
    const formattedDate = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    
    return `${formattedDate}, ${formattedTime}`;
  };

  const handleSearch = async () => {
    // Reset all warning and result states at the beginning of the search
    setSearchWarning(false);
    setEmptySearchWarning(false);
    setNoResults(false);
    setInvalidInputWarning('');
    setSearchResult([]);

    if (searchOption === "") {
      setSearchWarning(true);
      return;
    }

    if (!searchValue.trim()) {
      setEmptySearchWarning(true);
      return;
    }

    // Check for valid input based on search option
    if (searchOption === "purchase_wallet" && !walletRegex.test(searchValue)) {
      setInvalidInputWarning("Please enter a valid Ethereum wallet address.");
      return;
    }

    if (searchOption === "uid" && !uidRegex.test(searchValue)) {
      setInvalidInputWarning("Please enter a valid UID (28 alphanumeric characters).");
      return;
    }

    try {
      let nodePurchasedQuery;

      if (searchOption === "uid") {
        // Query node_purchased collection where uid matches searchValue
        nodePurchasedQuery = query(collection(db, "node_purchased"), where("uid", "==", searchValue));

        const querySnapshot = await getDocs(nodePurchasedQuery);
        const data = querySnapshot.docs.map(doc => {
          const docData = doc.data();
          const purchaseDateTime = docData.purchase_date_time instanceof Timestamp
            ? docData.purchase_date_time.toDate()
            : new Date(docData.purchase_date_time);
          const licenseType = toCamelCase(docData.license_type);
          const nodeType = toCamelCase(docData.node_type);
          return { 
            ...docData, 
            purchase_date_time: formatDateTime(purchaseDateTime), 
            license_type: licenseType, 
            node_type: nodeType 
          };
        });

        if (data.length === 0) {
          setNoResults(true);
        } else {
          setSearchResult(data);
        }
      } else if (searchOption === "purchase_wallet") {
        // Convert search value to lowercase
        const searchValueLowerCase = searchValue.toLowerCase();

        // Query users collection where wallet_lc matches searchValueLowerCase
        const userQuery = query(collection(db, "users"), where("wallet_lc", "==", searchValueLowerCase));
        const userQuerySnapshot = await getDocs(userQuery);

        if (!userQuerySnapshot.empty) {
          const userId = userQuerySnapshot.docs[0].data().user_id;

          // Query node_purchased collection where uid matches userId
          nodePurchasedQuery = query(collection(db, "node_purchased"), where("uid", "==", userId));

          const querySnapshot = await getDocs(nodePurchasedQuery);
          const data = querySnapshot.docs.map(doc => {
            const docData = doc.data();
            const purchaseDateTime = docData.purchase_date_time instanceof Timestamp
              ? docData.purchase_date_time.toDate()
              : new Date(docData.purchase_date_time);
            const licenseType = toCamelCase(docData.license_type);
            const nodeType = toCamelCase(docData.node_type);
            return { 
              ...docData, 
              purchase_date_time: formatDateTime(purchaseDateTime), 
              license_type: licenseType, 
              node_type: nodeType 
            };
          });

          if (data.length === 0) {
            setNoResults(true);
          } else {
            setSearchResult(data);
          }
        } else {
          setSearchResult([]);
          setNoResults(true); // Set no results state if no user matches
        }
      } else {
        // Invalid search option
        return;
      }
    } catch (error) {
      console.error("Error searching node_purchased:", error);
      setNoResults(true);
    }
  };

  // Function to convert string to camel case
  const toCamelCase = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };  

  // Function to determine rarity based on node number
  const getRarity = (nodeNumber) => {
    if (nodeNumber >= 1 && nodeNumber <= 3000) {
      return "Mythical";
    } else if (nodeNumber >= 3001 && nodeNumber <= 5000) {
      return "Legendary";
    } else if (nodeNumber >= 5001 && nodeNumber <= 8000) {
      return "Epic";
    } else if (nodeNumber >= 8001 && nodeNumber <= 30000) {
      return "Standard";
    } else {
      return "Unknown";
    }
  };

  return (
    <>
      <Modal
        activeModal={isOpen}
        onClose={onClose}
        title={"Search Node"}
        className="max-w-4xl"
      >
        <div> 
          <div className="flex items-center">
            <select 
              value={searchOption} 
              onChange={(e) => setSearchOption(e.target.value)} 
              className="mr-2 px-2 py-2 border rounded-md text-black-500"
            >
              <option value="" disabled selected>Search by</option>
              <option className="text-black-500" value="purchase_wallet">Purchase Wallet</option>
              <option className="text-black-500" value="uid">UID</option>
            </select>
            <input
              type="text"
              value={searchValue}
              placeholder="Search..."
              onChange={(e) => setSearchValue(e.target.value)}
              className="mr-2 px-2 py-1 border rounded-md w-full text-black-500"
            />
            <Button 
              onClick={handleSearch} 
              className="btn btn-primary btn-sm hover:bg-blue-600" 
              icon="heroicons-outline:search" 
            />
          </div>
          {searchWarning && (
            <p className="mt-6 text-red-500">Please select an option.</p>
          )}
          {emptySearchWarning && (
            <p className="mt-6 text-red-500">Please enter a search value.</p>
          )}
          {invalidInputWarning && (
            <p className="mt-6 text-red-500">{invalidInputWarning}</p>
          )}
          {noResults && searchValue.trim() !== "" && !emptySearchWarning && !invalidInputWarning && (
            <p className="mt-6 text-white">No results found.</p>
          )}
          {currentItems.length > 0 ? (
            <table className="mt-6 w-full table-auto border-collapse">
              <thead>
                <tr className='text-white text-sm xl:text-base gothamMedium'>
                  <th className="px-4 py-2">License Type</th>
                  <th className="px-4 py-2">Node Number</th>
                  <th className="px-4 py-2">Node Type</th>
                  <th className="px-4 py-2">Purchase Date Time</th>
                  <th className="px-4 py-2">Rarity</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((result, index) => (
                  <tr key={index} className='text-sm xl:text-base bg-metaxseed-content-gray gothamBook'>
                    <td className="px-4 py-4">{result.license_type}</td>
                    <td className="px-4 py-4">{result.node_number}</td> 
                    <td className="px-4 py-4">{result.node_type}</td>
                    <td className="px-4 py-4">{result.purchase_date_time}</td> 
                    <td className="px-4 py-4">{getRarity(result.node_number)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {/* Pagination component */}
          {searchResult.length > itemsPerPage && (
            <div className="mt-4 flex justify-center">
              <Pagination
                totalPages={Math.ceil(searchResult.length / itemsPerPage)}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
          {/* Close button */}
          <div className="flex justify-end mt-6">
            <button 
              onClick={onClose} 
              className="bg-gray-500 text-white px-3 py-2 rounded-md hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );  
};

export default SearchNodePurchasedModal;
